import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { CircularProgress, Grid, IconButton, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import {
  useCalcTaskCreate,
  useExpansionProductList,
  useExpansionRead,
  useExpansionUpdate,
  useGlobalSettingsRead,
  useProductUpdate,
  useRunningTask,
  useStockTaskCreate,
  useSyncTaskCreate,
} from "hooks";
import moment from "moment";
import {
  CreateCalcTaskInput,
  CreateStockTaskInput,
  CreateSyncTaskInput,
  OutOfStockSelection,
} from "types";
import {
  CustomButton,
  CustomLink,
  CustomSwitch,
  CustomTable,
  CustomTextButton,
} from "core";
import { DescriptionFormatter } from "components";
import { BoxContainer } from "layout/Container/BoxContainer";
import { getNextCalculationTime } from "modules/expansions/business";
import { EyeLineIcon, EyeLineOffIcon, OpenBrowserIcon } from "modules/icons";
import { isProductPriceRetryable } from "modules/products/business";
import { IconColumnFormatter } from "modules/shared/columnformatters/IconColumnFormatter";
import { getIntervalDescription } from "modules/shared/options/IntervalTypeOption";
import { Routes } from "routes";
import { RunningTaskBanner } from "../RunningTaskBanner";
import { CalcIntervalFormDialog } from "../calcintervalForm/CalcIntervalFormDialog";
import utils from "utils";
import useStyles from "./styles";

export const ExpansionDetails: React.FC = () => {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { pathname } = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  const { expansion } = useExpansionRead();
  // Ungefiltert (erster Parameter false): sonst würden der Massen-Reset und die
  // Anzahl im Sammel-Retry-Button dem gerade aktiven Tabellenfilter folgen - der
  // Zähler würde beim Drehen am Filter wandern und beim Filter
  // "Preis erfolgreich = ja" auf 0 fallen.
  const { expansionProductList } = useExpansionProductList(
    false,
    false,
    expansion?.id,
  );
  const updateExpansionMutation = useExpansionUpdate();
  const updateProductMutation = useProductUpdate();

  // const { runningSyncTask } = useSyncTaskRunningList();

  // Die globale Aufgabensperre wird nicht mehr vom Frontend geschrieben - die
  // Lambda erwirbt sie selbst konditional. Nur so sind Erwerb und Freigabe
  // atomar und symmetrisch, und das Fenster zwischen zwei Browser-Tabs ist zu.
  const { globalSettings } = useGlobalSettingsRead();
  const createCalcTaskMutation = useCalcTaskCreate();

  const createSyncTaskMutation = useSyncTaskCreate();
  const createStockTaskMutation = useStockTaskCreate();

  const [outOfStockSelectionEnabled, setOutOfStockSelectionEnabled] =
    useState<boolean>(false);

  const [isCalcIntervalDialogOpen, setIsCalcIntervalDialogOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (!expansion) return;

    setOutOfStockSelectionEnabled(
      expansion.outOfStockSelection === "Include" ? true : false,
    );
  }, [expansion]);

  const expansionActiveHandler = async () => {
    if (!expansion) return;

    const newActiveState = !expansion.isExpansionActive;

    updateExpansionMutation.mutate({
      id: expansion.id,
      isExpansionActive: newActiveState,
    });

    expansionProductList.forEach((product) => {
      updateProductMutation.mutate({
        id: product.id,
        expansionID: product.expansionID,
        isProductActive: newActiveState,
      });
    });

    searchParams.set("isProductActive", "all");
    sessionStorage.setItem(pathname, searchParams.toString());
    setSearchParams(searchParams);
  };

  const [isOutOfStockSelectionLoading, setIsOutOfStockSelectionLoading] =
    useState<boolean>(false);

  const expansionOutOfStockSelectionHandler = async (value: boolean) => {
    if (!expansion) return;

    setIsOutOfStockSelectionLoading(true);

    const newOutOfStockSelection: OutOfStockSelection = value
      ? "Include"
      : "Exclude";

    await updateExpansionMutation.mutateAsync({
      id: expansion.id,
      outOfStockSelection: newOutOfStockSelection,
    });

    setIsOutOfStockSelectionLoading(false);
  };

  // Abgeleitet aus den Aufgabenzeilen selbst statt aus GlobalSettings plus
  // 15-Minuten-Wanduhr: die Sperre wird erst gesetzt, wenn die Lambda anläuft.
  const { isTaskRunning, runningTask, isForeignExpansion } = useRunningTask(
    expansion?.id,
  );

  const retryableProducts = useMemo(
    () => expansionProductList.filter(isProductPriceRetryable),
    [expansionProductList],
  );

  const storePriceCalculationHandler = async () => {
    if (!expansion || !globalSettings || isTaskRunning) return;

    const isCalcedRecently = Boolean(
      expansion.lastStorePriceCalc &&
        moment().diff(moment(expansion.lastStorePriceCalc), "hours") <= 24,
    );

    if (isCalcedRecently) {
      enqueueSnackbar(
        "Die Ladenpreise wurden in den letzten 24 Stunden bereits aktualisiert.",
      );
      return;
    }

    const updateProductPromises = expansionProductList.map((product) =>
      updateProductMutation.mutateAsync({
        id: product.id,
        expansionID: product.expansionID,
        lastStorePriceCalcSuccess: false,
        priceStoreSetManually: false,
        // Muss mit zurückgesetzt werden: bliebe ein alter NoArticlesFound-Grund
        // stehen, wäre die Karte nach diesem Lauf dauerhaft vom Sammel-Retry
        // ausgeschlossen, obwohl sie nur noch nicht bearbeitet wurde.
        lastPriceCalcFailedReason: null,
      }),
    );

    await Promise.all(updateProductPromises);

    const createCalcTaskInput: CreateCalcTaskInput = {
      taskType: "Manually",
      expansionID: expansion.id,
      status: "Pending",
      lastTriggered: new Date().toISOString(),
      startTime: new Date().toISOString(),
    };

    const createdCalcTask =
      await createCalcTaskMutation.mutateAsync(createCalcTaskInput);

    if (!createdCalcTask) {
      enqueueSnackbar("Preisberechnung konnte nicht gestartet werden!");
      return;
    }

    await updateExpansionMutation.mutateAsync({
      id: expansion.id,
      nextOnlinePriceCalc: getNextCalculationTime(
        expansion.intervalType,
        expansion.intervalValue,
      ),
    });
  };

  /**
   * Neuberechnung nur für die fehlgeschlagenen Karten.
   *
   * Bewusst OHNE vier Dinge, die der volle Lauf tut:
   * 1. kein Massen-Reset aller Produkte - die Teilmenge steht schon auf false,
   *    und der Rest rot zu färben würde "nie bearbeitet" von "fehlgeschlagen"
   *    nicht mehr unterscheidbar machen;
   * 2. kein Zurücksetzen von priceStoreSetManually - das wäre stiller Verlust
   *    manuell gepflegter Preise;
   * 3. kein nextOnlinePriceCalc-Update - ein Teil-Retry ist kein geplanter Lauf;
   * 4. keine 24-Stunden-Prüfung - der Retry ist die Reaktion auf einen gerade
   *    gescheiterten Lauf und muss sofort möglich sein.
   */
  const retryFailedProductsHandler = async () => {
    if (!expansion || isTaskRunning || retryableProducts.length === 0) return;

    const createCalcTaskInput: CreateCalcTaskInput = {
      taskType: "Manually",
      expansionID: expansion.id,
      status: "Pending",
      lastTriggered: new Date().toISOString(),
      startTime: new Date().toISOString(),
      calcScope: "Products",
      productIDs: retryableProducts.map((product) => product.id),
    };

    const createdCalcTask =
      await createCalcTaskMutation.mutateAsync(createCalcTaskInput);

    if (!createdCalcTask) {
      enqueueSnackbar("Neuberechnung konnte nicht gestartet werden!");
      return;
    }

    enqueueSnackbar(
      `Neuberechnung für ${retryableProducts.length} Karte(n) gestartet.`,
      { variant: "info" },
    );
  };

  const synchronizationHandler = async () => {
    if (!expansion || !globalSettings || isTaskRunning) return;

    const createSyncTaskInput: CreateSyncTaskInput = {
      taskType: "Sync",
      expansionID: expansion.id,
      status: "Pending",
      startTime: new Date().toISOString(),
      syncExpansionWithoutProducts: false,
    };

    const createdSyncTask =
      await createSyncTaskMutation.mutateAsync(createSyncTaskInput);

    if (!createdSyncTask) {
      enqueueSnackbar(
        "Synchronisation der Karten konnte nicht gestartet werden!",
      );
      return;
    }
  };

  const stockSynchronizationHandler = async () => {
    if (!expansion || !globalSettings || isTaskRunning) return;

    const createStockTaskInput: CreateStockTaskInput = {
      taskType: "Stock",
      expansionID: expansion.id,
      status: "Pending",
      startTime: new Date().toISOString(),
    };

    const createdStockTask =
      await createStockTaskMutation.mutateAsync(createStockTaskInput);

    if (!createdStockTask) {
      enqueueSnackbar(
        "Synchronisation der Bestände konnte nicht gestartet werden!",
      );
      return;
    }
  };

  return (
    <BoxContainer
      boxAlignment="center"
      paddingHorizontal={30}
      paddingVertical={30}
      paddingTop={true}
    >
      {expansion && (
        <CalcIntervalFormDialog
          dialogOpen={isCalcIntervalDialogOpen}
          setDialogOpen={setIsCalcIntervalDialogOpen}
          expansion={expansion}
        />
      )}
      {/* Hier und nicht in der Kartenliste: ExpansionDetails steht über den Tabs
          und besitzt die drei Start-Buttons, die Leiste ist damit auf dem Karten-
          und dem Aufgaben-Tab sichtbar. */}
      <RunningTaskBanner
        runningTask={runningTask}
        isForeignExpansion={isForeignExpansion}
      />
      <Grid
        container
        direction="row"
        spacing={1}
        justifyContent="space-between"
      >
        <Grid item md={6}>
          <CustomTable
            tableTheme="default"
            marginBottom={0}
            tableClassName={classes.expansionDetilsTable}
          >
            <tbody>
              <tr>
                <td>
                  <Typography variant="h4">Editions-ID:</Typography>
                </td>
                <td>{expansion?.idExpansion}</td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">Editions-Icon:</Typography>
                </td>
                <td>
                  <IconColumnFormatter expansion={expansion} />
                </td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">Abkürzung:</Typography>
                </td>
                <td>{expansion?.abbreviation}</td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">Anzahl Karten:</Typography>
                </td>
                <td>{expansion?.productsCount}</td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">Edition aktiv:</Typography>
                </td>
                <td>
                  {expansion?.isExpansionActive ? "Ja" : "Nein"}
                  <IconButton
                    onClick={() => expansionActiveHandler()}
                    className={classes.iconButton}
                  >
                    {expansion?.isExpansionActive ? (
                      <EyeLineIcon className={classes.active} />
                    ) : (
                      <EyeLineOffIcon className={classes.inactive} />
                    )}
                  </IconButton>
                </td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">Release:</Typography>
                </td>
                <td>
                  {expansion?.releaseDate
                    ? utils.dates.getDateInGermanFromAWSDateFormat(
                        expansion.releaseDate,
                      )
                    : ""}
                </td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">Berechn.-Intervall:</Typography>
                </td>
                <td>
                  {expansion ? (
                    <CustomTextButton
                      text={getIntervalDescription(
                        expansion.intervalType,
                        expansion.intervalValue,
                      )}
                      onClick={() => setIsCalcIntervalDialogOpen(true)}
                      size="small"
                    />
                  ) : (
                    ""
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">Onlinepreise:</Typography>
                </td>
                <td>
                  {expansion ? (
                    <CustomTextButton
                      text={
                        "Nächste Berechnung am " +
                        utils.dates.getDateInGermanFromAWSDateFormat(
                          expansion.nextOnlinePriceCalc,
                        )
                      }
                      onClick={() => setIsCalcIntervalDialogOpen(true)}
                      size="small"
                    />
                  ) : (
                    ""
                  )}
                  <br />
                  <span className={classes.dateFromNow}>zul.</span>{" "}
                  {expansion?.lastOnlinePriceCalc
                    ? utils.dates.getDateInGermanFromAWSDateFormat(
                        expansion.lastOnlinePriceCalc,
                      )
                    : ""}
                  {expansion?.lastOnlinePriceCalc && (
                    <span className={classes.dateFromNow}>
                      ({utils.dates.getFromNow(expansion.lastOnlinePriceCalc)})
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">Synchronisation:</Typography>
                </td>
                <td>
                  <CustomButton
                    text="Synchronisiere Karten"
                    size="xsmall"
                    disabled={isTaskRunning}
                    onClick={synchronizationHandler}
                  />
                  <br />
                  <span className={classes.dateFromNow}>zul.</span>{" "}
                  {expansion?.lastSynced
                    ? utils.dates.getDateInGermanFromAWSDateFormat(
                        expansion.lastSynced,
                      )
                    : ""}
                  {expansion?.lastSynced && (
                    <span className={classes.dateFromNow}>
                      ({utils.dates.getFromNow(expansion.lastSynced)})
                    </span>
                  )}
                </td>
              </tr>
            </tbody>
          </CustomTable>
        </Grid>
        <Grid item md={6}>
          <CustomTable
            tableTheme="default"
            marginBottom={0}
            tableClassName={classes.expansionDetilsTable}
          >
            <tbody>
              <tr>
                <td>
                  <Typography variant="h4">Alias:</Typography>
                </td>
                <td>
                  <DescriptionFormatter
                    description={expansion?.expansionAlias}
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">DE-Name:</Typography>
                </td>
                <td>{expansion?.expansionDeName}</td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">EN-Name:</Typography>
                </td>
                <td>{expansion?.expansionEnName}</td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">Parent:</Typography>
                </td>
                <td>
                  {expansion?.parentID ? (
                    <CustomLink
                      link={Routes.expansions.subNavigations.expansion.path.replace(
                        ":expansionID",
                        expansion.parentID,
                      )}
                    />
                  ) : (
                    <DescriptionFormatter description={expansion?.parentID} />
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">Karten-Einkauf:</Typography>
                </td>
                <td>
                  <Grid container direction="row" alignItems="center">
                    <Grid item style={{ marginRight: 10 }}>
                      <CustomSwitch
                        name="outOfStockSelectionEnabled"
                        switchLabel={outOfStockSelectionEnabled ? "Ja" : "Nein"}
                        checkedValue={outOfStockSelectionEnabled}
                        onChange={(value) => {
                          setOutOfStockSelectionEnabled(value.target.checked);
                          expansionOutOfStockSelectionHandler(
                            value.target.checked,
                          );
                        }}
                        required={true}
                      />
                    </Grid>
                    {isOutOfStockSelectionLoading && (
                      <CircularProgress size={20} />
                    )}
                  </Grid>
                </td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">Webseite:</Typography>
                </td>
                <td>
                  <a
                    href={
                      "https://www.cardmarket.com" + expansion?.expansionWebsite
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.websiteLink}
                  >
                    {expansion?.expansionEnName}
                    <OpenBrowserIcon />
                  </a>
                </td>
              </tr>
              <tr>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">Ladenpreise:</Typography>
                </td>
                <td>
                  <CustomButton
                    text="Aktualisiere Laden- und Onlinepreise"
                    size="xsmall"
                    disabled={isTaskRunning}
                    onClick={storePriceCalculationHandler}
                  />
                  <br />
                  {/* Anzahl im Text, damit vor dem Start sichtbar ist, wie viele
                      Karten betroffen sind - ohne Dialog. */}
                  <CustomButton
                    text={`Fehlgeschlagene neu berechnen (${retryableProducts.length})`}
                    size="xsmall"
                    color="yellow"
                    disabled={isTaskRunning || retryableProducts.length === 0}
                    onClick={retryFailedProductsHandler}
                  />
                  <br />
                  <span className={classes.dateFromNow}>zul.</span>{" "}
                  {expansion?.lastStorePriceCalc
                    ? utils.dates.getDateInGermanFromAWSDateFormat(
                        expansion.lastStorePriceCalc,
                      )
                    : ""}
                  {expansion?.lastStorePriceCalc && (
                    <span className={classes.dateFromNow}>
                      ({utils.dates.getFromNow(expansion.lastStorePriceCalc)})
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  <Typography variant="h4">Bestände:</Typography>
                </td>
                <td>
                  <CustomButton
                    text="Aktualisiere Bestände"
                    size="xsmall"
                    disabled={isTaskRunning}
                    onClick={stockSynchronizationHandler}
                  />
                  <br />
                  <span className={classes.dateFromNow}>zul.</span>{" "}
                  {expansion?.lastStockSynced
                    ? utils.dates.getDateInGermanFromAWSDateFormat(
                        expansion.lastStockSynced,
                      )
                    : ""}
                  {expansion?.lastStockSynced && (
                    <span className={classes.dateFromNow}>
                      ({utils.dates.getFromNow(expansion.lastStockSynced)})
                    </span>
                  )}
                </td>
              </tr>
            </tbody>
          </CustomTable>
        </Grid>
      </Grid>
    </BoxContainer>
  );
};
